import { PlainDate, TIMEUNIT_DAYS } from "@kiss-solutions/plaindate";
import * as yup from "yup";
import { i18_ARTIST, i18_TRANSLATION } from "../i18nReferences";
import { keyBy, pick } from "../store/utils/lodash-replacement";
import { formatDate, reduceIsoDateToDateString, toCalendarDayDate, toDateString } from "./date";
import { GUTSCHEINVERKAUF_STUDIOKASSE } from "./studio";
import { getPrivateMailColumnText } from "../components/artist/mail/artistMailModel";
import {
    isStandardLocationShiftMissing,
    STANDARD_LOCATION_SHIFT_REQUIRED_KEY,
} from "../components/common/location/standardLocationShiftRule";

export const ARTIST_VERTRAGSTYP_SELBSTAENDIG = 1;
export const ARTIST_VERTRAGSTYP_ANGESTELLT = 2;
export const ARTIST_VERTRAGSTYP_MAP = { ARTIST_VERTRAGSTYP_SELBSTAENDIG, ARTIST_VERTRAGSTYP_ANGESTELLT };

const isLocationInZeitraum = (zeitraum, location) =>
    reduceIsoDateToDateString(location.DatVon) < zeitraum.end &&
    reduceIsoDateToDateString(location.DatBis) >= zeitraum.start;

export const isArtistAtLocationInZeitraum = (artist, tatortId, zeitraum, ausgeschiedene = false) => {
    if (artist.Ausgeschieden && !ausgeschiedene) {
        return false;
    }
    if (
        tatortId < 0 ||
        (artist.StdOrtID === tatortId &&
            artist.artLocList.find((l) => tatortId !== l.TatOrtID && isLocationInZeitraum(zeitraum, l)) === undefined)
    ) {
        return true;
    }
    const location = artist.artLocList.find((l) => tatortId === l.TatOrtID && isLocationInZeitraum(zeitraum, l));

    return location !== undefined;
};

/**
 * Artist location dates are calendar-only day values.
 *
 * IMPORTANT:
 * Backend `DatVon` / `DatBis` must be treated as timezone-agnostic dates.
 * Never parse `YYYY-MM-DD` with the native `Date` constructor here because that
 * introduces UTC/local timezone semantics and can shift the day by one.
 * Use `toCalendarDayDate(...)` so the calendar keeps the exact day from the backend.
 */
function normalizeArtistLocationDate(value) {
    return toCalendarDayDate(value);
}

export const getArtistLocation = (date, artist, artistLocations, tatortMap) => {
    const lookupDate = normalizeArtistLocationDate(date);
    let location = (artistLocations || []).find(
        (l) => {
            const datVon = normalizeArtistLocationDate(l.DatVon);
            const datBis = normalizeArtistLocationDate(l.DatBis);
            return (
                lookupDate &&
                datVon &&
                datBis &&
                toDateString(lookupDate) >= toDateString(datVon) &&
                toDateString(lookupDate) <= toDateString(datBis)
            );
        }
    );
    if (!location) {
        location = tatortMap[artist.StdOrtID];
    }
    return tatortMap[location.TatOrtID];
};

const toComparableArtistLocation = (location) => {
    if (!location) {
        return null;
    }
    const datVon = normalizeArtistLocationDate(location.DatVon);
    const datBis = normalizeArtistLocationDate(location.DatBis);
    if (!datVon || !datBis) {
        return null;
    }
    return {
        ...location,
        DatVon: datVon,
        DatBis: datBis,
    };
};

export const findOverlappingArtistLocation = ({ datVon, datBis, artAtOrtID, allLocations = [] }) => {
    const comparableDatVon = normalizeArtistLocationDate(datVon);
    const comparableDatBis = normalizeArtistLocationDate(datBis);
    if (!comparableDatVon || !comparableDatBis) {
        return undefined;
    }
    const pdDatVon = new PlainDate(comparableDatVon).startOfDay();
    const pdDatBisExclusive = new PlainDate(comparableDatBis).add(1, TIMEUNIT_DAYS).startOfDay();
    return allLocations
        .map(toComparableArtistLocation)
        .filter(Boolean)
        .find(
            (location) =>
                location.ArtAtOrtID !== artAtOrtID &&
                pdDatBisExclusive.isAfter(new PlainDate(location.DatVon).startOfDay(), TIMEUNIT_DAYS) &&
                new PlainDate(location.DatBis).startOfDay().add(1, TIMEUNIT_DAYS).isAfter(pdDatVon, TIMEUNIT_DAYS)
        );
};

export const getArtistLocationValidation = ({ location, artistLocations = [], stdOrtId, hasKissSign }) => {
    const datVon = normalizeArtistLocationDate(location?.DatVon);
    const datBis = normalizeArtistLocationDate(location?.DatBis);
    const hasDateRange = Boolean(datVon && datBis);
    const isDateRangeValid = hasDateRange ? datVon.getTime() <= datBis.getTime() : false;
    const overlappingLocation = isDateRangeValid
        ? findOverlappingArtistLocation({
            datVon,
            datBis,
            artAtOrtID: location?.ArtAtOrtID,
            allLocations: artistLocations,
        })
        : undefined;
    const isStandardLocationShiftInvalid = isStandardLocationShiftMissing({
        hasKissSign,
        stdOrtId,
        tatortId: location?.TatOrtID,
        shiftId: location?.shiftId ?? null,
    });
    return {
        datVon,
        datBis,
        hasDateRange,
        isDateRangeValid,
        overlappingLocation,
        isOverlapping: Boolean(overlappingLocation),
        isStandardLocationShiftInvalid,
        hasRequiredFields: Boolean(datVon && datBis && location?.TatOrtID),
    };
};

export const getIntersectingLocations = (von, bis, artist, ArtAtOrtID) =>
    findOverlappingArtistLocation({
        datVon: von,
        datBis: bis,
        artAtOrtID: ArtAtOrtID,
        allLocations: artist?.artLocList || [],
    });

export const getArtistsStdPreisFor = (artist, anzTermine) =>
    artist ? (anzTermine > 1 ? artist.Preis : artist.PreisSingle) : "";
export const extractArtistSettingsFromArtistMap = (artistMap) =>
    Object.keys(artistMap).reduce(
        (pv, cv) => ({
            ...pv,
            [cv]: {
                ManageAZ: artistMap[cv].ManageAZ,
                EditIncome: artistMap[cv].EditIncome,
                Kuerzel: artistMap[cv].Kuerzel,
                Vertragstyp: artistMap[cv].Vertragstyp,
                StdOrtID: artistMap[cv].StdOrtID,
            },
        }),
        {}
    );

export const getArtistTatorte = (artistMap, tatOrtMap, studioTatOrtID) => {
    if (!artistMap || !tatOrtMap) {
        return [];
    }
    const tatOrtSortKeys = keyBy(
        Object.values(tatOrtMap).map((t) => ({
            id: t.TatOrtID,
            sortKey: t.IstStudioEigen ? (studioTatOrtID === t.TatOrtID ? "0" : "1") + t.Ort : "2" + t.Ort,
        })),
        "id"
    );
    const tatortIds = [...new Set(
        Object.values(artistMap)
            .filter((t) => t != null && t.StdOrtID != null)
            .map((t) => t.StdOrtID)
    )];
    return tatortIds.sort((id1, id2) => {
        const s1 = tatOrtSortKeys[id1];
        const s2 = tatOrtSortKeys[id2];
        if (!s1 || !s2) return 0;
        return s1.sortKey > s2.sortKey ? 1 : -1;
    });
};

export const convertLocationDates = (location) => ({
    ...location,
    DatVon: toCalendarDayDate(location.DatVon),
    DatBis: toCalendarDayDate(location.DatBis),
});

/**
 * POST/PUT `artists/:id/locations` request body: snake_case `shift_id`, date strings.
 * Input contract: UI / in-memory location uses camelCase `shiftId` only; any `shift_id` on the object is ignored.
 */
export const convertArtistLocation2DB = (artistLocation) => {
    const { shiftId, ...rawArtistLocation } = artistLocation;
    const rest = { ...rawArtistLocation };
    delete rest.shift_id;
    const raw = shiftId != null && shiftId !== "" ? Number(shiftId) : null;
    const normalized = raw != null && !Number.isNaN(raw) ? raw : null;
    return {
        ...rest,
        DatVon: toDateString(artistLocation.DatVon),
        DatBis: toDateString(artistLocation.DatBis),
        shift_id: normalized,
    };
};

const passwordConfirmationSchema = (schema, pwd) =>
    schema.oneOf([pwd], { key: "YUP.ERROR.PASSWORD_DIFFERS" }).required();

export const createArtistBaseSchema = () =>
    yup.object().shape({
        Kuerzel: yup
            .string()
            .transform((k) => k && k.trim())
            .min(2)
            .max(8)
            .when(["$allOtherArtistKuerzel"], ([allOtherKuerzel], schema) =>
                schema.notOneOf(allOtherKuerzel, { key: "YUP.ERROR.KUERZEL_IN_USE" })
            )
            .required(),
        Artist: yup.string().min(2).max(40).required(),
        Bank: yup.string().max(200).nullable(),
        IBAN: yup.string().max(22).nullable(),
        Adresse: yup.string().max(500).nullable(),
        Geburtstag: yup.date().max(new Date()).nullable(),
        Anzahlung: yup.number().min(0).required(),
        PreisLabel: yup.string().max(15).required(),
        Preis: yup.string().max(30).required(),
        PreisSingle: yup.string().max(30).required(),
        StdOrtID: yup
            .number()
            .when(["Vertragstyp", "$studioLocationIds"], ([vertragstyp, studioLocationIds], schema) =>
                vertragstyp === ARTIST_VERTRAGSTYP_ANGESTELLT
                    ? schema.oneOf(studioLocationIds, { key: "YUP.ERROR.ANGESTELLTE_NUR_IN_STUDIOLOCATIONS" })
                    : schema.min(1)
            ),
        BIC: yup.string().max(12).nullable(),
        Passwort: yup
            .string()
            .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,25})/, { message: { key: "YUP.ERROR.PASSWORD_FORMAT" } })
            .nullable(),
        PasswortBestaetigung: yup
            .string()
            .when(["Passwort"], ([pwd], schema) => (pwd ? passwordConfirmationSchema(schema, pwd) : schema.optional())),
    });

export const createArtistHoursSchema2 = () =>
    yup.object().shape({
        Anmerkung: yup.string().max(150).required(),
        Ab: yup
            .date()
            .test("onlyMondays", { key: "YUP.ERROR.ONLY_MONDAYS" }, (d) => d != null && d.getDay() === 1)
            .required(),
        ArbVon1: yup.date().required(),
        ArbBis1: yup.date().min(yup.ref("ArbVon1")).required(),
        ArbVon2: yup.date().required(),
        ArbBis2: yup.date().min(yup.ref("ArbVon2")).required(),
        ArbVon3: yup.date().required(),
        ArbBis3: yup.date().min(yup.ref("ArbVon3")).required(),
        ArbVon4: yup.date().required(),
        ArbBis4: yup.date().min(yup.ref("ArbVon4")).required(),
        ArbVon5: yup.date().required(),
        ArbBis5: yup.date().min(yup.ref("ArbVon5")).required(),
        ArbVon6: yup.date().required(),
        ArbBis6: yup.date().min(yup.ref("ArbVon6")).required(),
        ArbVon7: yup.date().required(),
        ArbBis7: yup.date().min(yup.ref("ArbVon7")).required(),
    });

export const convertArtistToSchema = (artistData, fields) => {
    const artist = {
        ...pick(artistData, fields),
    };
    if (fields.includes("Geburtstag")) {
        artist.Geburtstag = artistData.Geburtstag ? new PlainDate(artistData.Geburtstag).toLocalDate() : null;
    }
    if (fields.includes("PreferredTermintyp")) {
        artist.PreferredTermintyp = artistData.PreferredTermintyp || "";
    }
    return artist;
};

export const convertArtistToDB = (artistData) => ({
    ...artistData,
    Geburtstag: artistData.Geburtstag ? toDateString(artistData.Geburtstag) : null,
});

export const convertArtWorkTime = (d) => new Date(d.substring(0, 16).replace("T", " "));

export const convertArtistHoursToSchema = (artistData) =>
    artistData &&
    Object.keys(artistData).reduce((result, key) => {
        if (key.startsWith("ArbVon") || key.startsWith("ArbBis")) {
            result[key] = convertArtWorkTime(artistData[key]);
        } else if (key === "Ab") {
            result[key] = new PlainDate(artistData[key]).toLocalDate();
        } else {
            result[key] = artistData[key];
        }
        return result;
    }, {});

const convertArtWorkTimeBack = (d) => `1899-12-30T${d.toLocaleTimeString("de")}.000Z`;
export const getStdWorkTimes = () => {
    const result = [1, 2, 3, 4, 5, 6].reduce((result, day) => {
        result[`ArbVon${day}`] = "1899-12-30T10:00:00.000Z";
        result[`ArbBis${day}`] = "1899-12-30T18:00:00.000Z";
        return result;
    }, {});
    result.ArbVon7 = "1899-12-30T00:00:00.000Z";
    result.ArbBis7 = "1899-12-30T00:00:00.000Z";
    result.Pause = 60;
    return result;
};
export const convertFromArtistHoursSchema = (schemaData) =>
    schemaData &&
    Object.keys(schemaData).reduce((result, key) => {
        if (key.startsWith("ArbVon") || key.startsWith("ArbBis")) {
            result[key] = convertArtWorkTimeBack(schemaData[key]);
        } else if (key === "Ab") {
            result[key] = schemaData.Ab.toISOString();
        } else {
            result[key] = schemaData[key];
        }
        return result;
    }, {});

/**
 * Map artist location row from GET artists / `artLocList` or GET `artists/:id/locations`
 * into RHF default values. API contract: optional `shift_id` only (snake_case).
 * Output uses `shiftId` (camelCase) only.
 */
export const convertArtistLocationToSchema = (location) => {
    if (!location) {
        return location;
    }
    const { shift_id, ...rawLocation } = location;
    const rest = { ...rawLocation };
    const parsedShiftId =
        shift_id != null && shift_id !== "" ? Number(shift_id) : null;
    const shiftId = parsedShiftId != null && !Number.isNaN(parsedShiftId) ? parsedShiftId : null;
    return {
        ...rest,
        DatVon: normalizeArtistLocationDate(location.DatVon),
        DatBis: normalizeArtistLocationDate(location.DatBis),
        shiftId,
    };
};

/**
 * Normalizes an artist-location object into the frontend CRUD/form shape.
 *
 * Supported inputs:
 * - API rows with snake_case `shift_id`
 * - in-memory drafts with camelCase `shiftId`
 *
 * Dates are converted into local Date objects without changing the calendar day.
 */
export const convertArtistLocationDraftToSchema = (location) => {
    const normalizedLocation = convertArtistLocationToSchema(location);
    if (!location || !Object.prototype.hasOwnProperty.call(location, "shiftId")) {
        return normalizedLocation;
    }
    return {
        ...normalizedLocation,
        shiftId: location.shiftId ?? null,
    };
};

export const overlappingPeriod = (datVon, datBis, artAtOrtID, allLocations) =>
    findOverlappingArtistLocation({
        datVon,
        datBis,
        artAtOrtID,
        allLocations,
    });

/**
 * Display string for an artist location row. Ort is resolved from tatortMap (artLocList entries do not carry Ort).
 */
export const formatArtistLocation = (locale, timezone, location, tatortMap) => {
    if (!location) {
        return "";
    }
    const toDate = (d) => (d instanceof Date ? d : d ? new Date(d) : null);
    const vonStr = formatDate(toDate(location.DatVon), locale, timezone);
    const bisStr = formatDate(toDate(location.DatBis), locale, timezone);
    const ort = tatortMap?.[location.TatOrtID]?.Ort ?? "";
    const period = vonStr && bisStr ? `${vonStr} - ${bisStr}` : "";
    return ort ? `${period} ${ort}`.trim() : period;
};

export const createArtistLocationSchema = () =>
    yup
        .object()
        .shape({
            DatVon: yup.date().required(),
            DatBis: yup.date().min(yup.ref("DatVon"), { key: "YUP.ERROR.START_AFTER_END" }).required(),
            TatOrtID: yup.number().required(),
            shiftId: yup.number().nullable(),
        })
        .test("standard-location-shift-required", function (value) {
            const context = this?.options?.context || {};
            const validation = getArtistLocationValidation({
                location: value,
                artistLocations: context.artistLocations || [],
                stdOrtId: context.StdOrtID,
                hasKissSign: context.hasKissSign,
            });
            if (!validation.isStandardLocationShiftInvalid) {
                return true;
            }
            return this.createError({
                path: "TatOrtID",
                message: { key: STANDARD_LOCATION_SHIFT_REQUIRED_KEY, ns: i18_TRANSLATION },
            });
        });

import { RECHT_LESEN as LESERECHT, RECHT_SCHREIBEN as SCHREIBRECHT, RECHT_LOESCHEN as LOESCHRECHT } from "./permissions";
export { LESERECHT, SCHREIBRECHT, LOESCHRECHT };

export const checkForUpgrades = (neueRechte, oldRechte) => {
    const result = { ...neueRechte };
    if ((result.Rechte & LOESCHRECHT) > 0 && (oldRechte.Rechte & LOESCHRECHT) === 0) {
        result.Rechte = result.Rechte | SCHREIBRECHT | LESERECHT;
    }
    if (result.EinnBearbeiten && !oldRechte.EinnBearbeiten) {
        result.Rechte = result.Rechte | LESERECHT | SCHREIBRECHT;
    }
    if ((result.Rechte & SCHREIBRECHT) > 0 && (result.Rechte & SCHREIBRECHT) === 0) {
        result.Rechte = result.Rechte | LESERECHT;
    }

    if ((result.Rechte & LESERECHT) === 0 && (result.Rechte & SCHREIBRECHT) > 0) {
        result.Rechte = result.Rechte & ~SCHREIBRECHT;
    }
    if ((result.Rechte & SCHREIBRECHT) === 0 && (result.Rechte & LOESCHRECHT) > 0) {
        result.Rechte = result.Rechte & ~LOESCHRECHT;
    }
    if ((result.Rechte & SCHREIBRECHT) === 0 && result.EinnBearbeiten) {
        result.EinnBearbeiten = false;
    }
    return result;
};

export const createArtistAssistScheme = () =>
    yup.object().shape({
        EditIncome: yup.boolean().required(),
        AutoDayClose: yup.boolean().required(),
        ManageAZ: yup.boolean().required(),
    });

export const createArtistMapForGutscheinausgabe = (artistMap, studioGutscheinverkauf, inludedArtistId) =>
    Object.fromEntries(
        Object.entries(artistMap).filter(
            ([, a]) =>
                studioGutscheinverkauf === GUTSCHEINVERKAUF_STUDIOKASSE ||
                a.GetVoucher ||
                (inludedArtistId && inludedArtistId === a.ArtistID)
        )
    );

/**
 * @param {Object} params
 * @param {Function} params.t translation function
 * @param {Object} params.tatortMap locations by id
 * @param {boolean} params.isGoogleSyncEnabled whether the Google sync column is shown
 * @param {Object} [params.privateMailMap] personal addresses by artist id; the column "personal address" only
 *   exists when it is given (admins), because the addresses are not loaded for anybody else
 * @param {Function} [params.formatDate] formats a Date as a date in the studio's locale and time zone
 */
export const getArtistColumns = ({ t, tatortMap, isGoogleSyncEnabled, privateMailMap, formatDate }) => {
    const privateMailColumns = privateMailMap
        ? [
              {
                  field: "PrivateMail",
                  headerName: t("EDIT_ARTIST.PRIVATE_MAIL_COLUMN", {
                      ns: i18_ARTIST,
                      defaultValue: "Persönliche Adresse",
                  }),
                  // The value is the displayed text, so that sorting and filtering work on what the user sees.
                  valueGetter: (_value, row) => getPrivateMailColumnText(privateMailMap[row.ArtistID], t, formatDate),
                  minWidth: 240,
              },
          ]
        : [];
    const coldef = [
        { field: "Kuerzel", headerName: t("EDIT_ARTIST.KUERZEL_LABEL", { ns: i18_ARTIST }) },
        { field: "Artist", headerName: t("EDIT_ARTIST.ARTIST_LABEL", { ns: i18_ARTIST }), minWidth: 200 },
        {
            field: "StdOrtID",
            headerName: t("EDIT_ARTIST.LOCATION_LABEL", { ns: i18_ARTIST }),
            valueGetter: (value) => tatortMap[value].Ort,
            minWidth: 200,
        },
        {
            field: "MailAdr",
            headerName: t("EDIT_ARTIST.SENDER_MAIL_COLUMN", { ns: i18_ARTIST, defaultValue: "Absenderadresse" }),
            minWidth: 200,
        },
        ...privateMailColumns,
        {
            field: "Ausgeschieden",
            type: "boolean",
            headerName: t("EDIT_ARTIST.AUSGESCHIEDEN_LABEL", { ns: i18_ARTIST }),
        },
        { field: "isAdmin", type: "boolean", headerName: t("EDIT_ARTIST.ISADMIN_LABEL", { ns: i18_ARTIST }) },
        { field: "GetVoucher", type: "boolean", headerName: t("EDIT_ARTIST.GUTSCHEIN_LABEL", { ns: i18_ARTIST }) },
    ];
    if (isGoogleSyncEnabled) {
        coldef.push({ field: "GglSyncEnabled", type: "boolean", headerName: "Google Sync" });
    }
    return coldef;
};

export const sortByKuerzel = (a1, a2) => (a1.Kuerzel > a2.Kuerzel ? 1 : -1);

/** Whether the sender address of the artist can send: it is set and its access data is set up. */
export const isCommonArtistMailConfigSet = (artist) => Boolean(artist?.AbsenderVersandEingerichtet);

